// Shared product Excel format: export and import use exactly the same sheets / columns / headers.
export const PRODUCT_SHEET = 'محصولات'
export const VARIANT_SHEET = 'تنوع‌ها'

export const PRODUCT_COLUMNS = [
    {key: 'id', title: 'شناسه', aliases: ['id'], type: 'int', width: 8},
    {key: 'name', title: 'نام', aliases: ['name'], type: 'text', width: 40},
    // reference only (ignored on import): product type ساده / متغیر / رزرو (old headers: تنوع, variant_info)
    {key: 'variant_info', title: 'نوع محصول', aliases: ['product_type', 'تنوع', 'variant_info', 'variants_info'], type: 'text', width: 11, readOnly: true},
    {key: 'language', title: 'زبان', aliases: ['language'], type: 'lookup', width: 10},
    {key: 'brand', title: 'برند', aliases: ['brand'], type: 'lookup', width: 16},
    {key: 'categories', title: 'دسته‌بندی', aliases: ['category', 'categories', 'دسته بندی'], type: 'categories', width: 36},
    {key: 'price', title: 'قیمت', aliases: ['price'], type: 'money', width: 14},
    {key: 'offer', title: 'تخفیف', aliases: ['offer'], type: 'money', width: 12},
    {key: 'quantity', title: 'موجودی', aliases: ['quantity', 'تعداد'], type: 'quantity', width: 10},
    {key: 'status', title: 'وضعیت انتشار', aliases: ['status', 'وضعیت'], type: 'status', width: 13},
    {key: 'abstract', title: 'توضیح کوتاه', aliases: ['abstract'], type: 'text', width: 30},
    {key: 'description', title: 'توضیحات', aliases: ['description'], type: 'text', width: 40},
    {key: 'thumbnail', title: 'تصویر شاخص', aliases: ['thumbnail'], type: 'text', width: 40},
    {key: 'gallery', title: 'گالری', aliases: ['gallery', 'images'], type: 'text', width: 40},
    {key: 'length', title: 'طول', aliases: ['length'], type: 'number', width: 8},
    {key: 'width', title: 'عرض', aliases: ['width'], type: 'number', width: 8},
    {key: 'height', title: 'ارتفاع', aliases: ['height'], type: 'number', width: 8},
    {key: 'weight', title: 'وزن', aliases: ['weight'], type: 'int', width: 8},
]

export const VARIANT_COLUMNS = [
    {key: 'product_id', title: 'شناسه محصول', aliases: ['product_id'], type: 'int', width: 12},
    {key: 'product_name', title: 'نام محصول', aliases: ['product_name'], type: 'text', width: 36, readOnly: true, sendOnImport: true},
    {key: 'variant_id', title: 'شناسه تنوع', aliases: ['variant_id'], type: 'int', width: 11},
    {key: 'label', title: 'تنوع', aliases: ['label', 'variant'], type: 'text', width: 34},
    {key: 'price', title: 'قیمت', aliases: ['price'], type: 'money', width: 14},
    {key: 'offer', title: 'تخفیف', aliases: ['offer'], type: 'money', width: 12},
    {key: 'quantity', title: 'موجودی', aliases: ['quantity', 'تعداد'], type: 'quantity', width: 10},
    {key: 'stock_status', title: 'وضعیت موجودی', aliases: ['stock_status'], type: 'stock', width: 13, readOnly: true},
]

const UNLIMITED = ['نامحدود', 'unlimited', 'بینهایت', 'بی‌نهایت']
const PUBLISHED = ['1', 'منتشر', 'منتشرشده', 'منتشر شده', 'فعال', 'published', 'active', 'true', 'بله']
const DRAFT = ['0', 'پیش‌نویس', 'پیش نویس', 'پیشنویس', 'غیرفعال', 'غیر فعال', 'draft', 'inactive', 'false', 'خیر']

export function normalizeDigits(value) {
    return String(value)
        .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
        .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
}

export function normalizeLetters(value) {
    return String(value).replace(/[يى]/g, 'ی').replace(/ك/g, 'ک')
}

function headerKey(value) {
    return normalizeLetters(normalizeDigits(value == null ? '' : value))
        .replace(/^\uFEFF/, '')
        .replace(/[\s\u200c\u200f\u200e_\-]/g, '')
        .toLowerCase()
}

function compareKey(value) {
    return normalizeLetters(String(value)).replace(/[\s\u200c]/g, '').toLowerCase()
}

function isEmpty(value) {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

export function parseNumber(value, {unlimited = false, integer = false} = {}) {
    if (isEmpty(value)) return null
    if (typeof value === 'number') return integer ? Math.round(value) : value
    const text = normalizeDigits(normalizeLetters(String(value))).trim()
    if (unlimited && UNLIMITED.some((word) => compareKey(word) === compareKey(text))) return null
    const cleaned = text.replace(/تومان|ریال/g, '').replace(/[,٬،'\s]/g, '').replace(/٫/g, '.')
    if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
        const number = Number(cleaned)
        return integer ? Math.round(number) : number
    }
    return String(value).trim()
}

export function parseStatus(value) {
    if (isEmpty(value)) return null
    if (typeof value === 'number') return value
    if (typeof value === 'boolean') return value ? 1 : 0
    const key = compareKey(normalizeDigits(value).trim())
    if (PUBLISHED.some((word) => compareKey(word) === key)) return 1
    if (DRAFT.some((word) => compareKey(word) === key)) return 0
    return String(value).trim()
}

function cleanLookup(value) {
    if (isEmpty(value)) return null
    return normalizeLetters(String(value)).trim()
}

function splitCategories(value, legacyHeader) {
    if (isEmpty(value)) return []
    const text = String(value)
    const separator = text.indexOf('|') !== -1 || !legacyHeader ? '|' : ','
    return text.split(separator)
        .map((path) => normalizeLetters(path).split('>').map((part) => part.trim()).filter(Boolean).join(' > '))
        .filter(Boolean)
}

/** map sheet header names (Persian or old English) to column keys */
// continuation columns of long texts: «توضیحات (ادامه ۲)», «توضیحات (ادامه ۳)», ...
const CONTINUATION_RE = /^(.*?)\s*\(\s*(?:ادامه|cont(?:inued)?|part)\s*(\d+)\s*\)\s*$/i

function continuationTitle(title, part) {
    return `${title} (ادامه ${String(part).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])})`
}

function headerMap(headers, columns) {
    const map = {}
    const parts = {}
    headers.forEach((header, index) => {
        const match = normalizeLetters(normalizeDigits(header == null ? '' : header)).trim().match(CONTINUATION_RE)
        if (match) {
            const baseKey = headerKey(match[1])
            const base = columns.find((col) => headerKey(col.title) === baseKey || col.aliases.some((alias) => headerKey(alias) === baseKey))
            if (base) {
                (parts[base.key] = parts[base.key] || []).push({part: Number(match[2]), index})
                return
            }
        }
        const key = headerKey(header)
        const column = columns.find((col) => headerKey(col.title) === key || col.aliases.some((alias) => headerKey(alias) === key))
        if (column && map[column.key] === undefined) {
            map[column.key] = {index, legacy: headerKey(column.title) !== key}
        }
    })
    Object.keys(parts).forEach((key) => {
        if (map[key]) map[key].parts = parts[key].sort((a, b) => a.part - b.part).map((item) => item.index)
    })
    return map
}

/** cell value of a column; long texts split over continuation columns are joined back in order */
function readCell(row, position) {
    const first = row[position.index]
    if (!position.parts || !position.parts.length) return first
    const pieces = [first].concat(position.parts.map((index) => row[index]))
    if (pieces.every(isEmpty)) return first
    return pieces.map((value) => (value === null || value === undefined ? '' : String(value))).join('')
}

function sheetRows(XLSX, sheet) {
    return XLSX.utils.sheet_to_json(sheet, {header: 1, defval: null, raw: true, blankrows: false})
}

function convert(column, raw, legacy) {
    switch (column.type) {
        case 'int':
            return parseNumber(raw, {integer: true})
        case 'money':
            return parseNumber(raw, {integer: true})
        case 'quantity':
            return parseNumber(raw, {unlimited: true, integer: true})
        case 'number':
            return parseNumber(raw)
        case 'status':
            return parseStatus(raw)
        case 'lookup':
            return cleanLookup(raw)
        case 'categories':
            return splitCategories(raw, legacy)
        default:
            return isEmpty(raw) ? null : String(raw).trim()
    }
}

function findSheet(workbook, names) {
    const keys = names.map(headerKey)
    return workbook.SheetNames.find((name) => keys.indexOf(headerKey(name)) !== -1)
}

/** parse a workbook into import payloads: { products: [...], variants: [...] } */
export function parseProductWorkbook(XLSX, workbook) {
    const productSheetName = findSheet(workbook, [PRODUCT_SHEET, 'products']) || workbook.SheetNames[0]
    const variantSheetName = findSheet(workbook, [VARIANT_SHEET, 'تنوعها', 'variants'])

    const products = []
    const productMeta = []
    const productRows = sheetRows(XLSX, workbook.Sheets[productSheetName])
    const productHeaders = productRows.length ? productRows[0] : []
    if (productRows.length) {
        const map = headerMap(productRows[0], PRODUCT_COLUMNS)
        productRows.slice(1).forEach((row, i) => {
            if (!row || row.every(isEmpty)) return
            const item = {}
            PRODUCT_COLUMNS.forEach((column) => {
                const position = map[column.key]
                if (column.readOnly) return
                if (position === undefined) {
                    if (column.key === 'categories') item.categories = []
                    return
                }
                const raw = readCell(row, position)
                if (isTruncatedCell(raw)) return // old exports cut long texts: keep the site value
                item[column.key] = convert(column, raw, position.legacy)
            })
            if (typeof item.name === 'number') item.name = String(item.name)
            if (item.brand !== undefined && item.brand !== null) item.brand = String(item.brand)
            products.push(item)
            productMeta.push({row: i + 2, name: item.name, raw: row})
        })
    }

    const variants = []
    const variantMeta = []
    let variantHeaders = []
    if (variantSheetName && variantSheetName !== productSheetName) {
        const variantRows = sheetRows(XLSX, workbook.Sheets[variantSheetName])
        if (variantRows.length) {
            variantHeaders = variantRows[0]
            const map = headerMap(variantRows[0], VARIANT_COLUMNS)
            const nameColumn = map.product_name
            variantRows.slice(1).forEach((row, i) => {
                if (!row || row.every(isEmpty)) return
                const item = {}
                VARIANT_COLUMNS.forEach((column) => {
                    const position = map[column.key]
                    if ((column.readOnly && !column.sendOnImport) || position === undefined) return
                    const raw = readCell(row, position)
                    if (isTruncatedCell(raw)) return
                    item[column.key] = convert(column, raw, position.legacy)
                })
                if (item.offer === null) item.offer = 0
                variants.push(item)
                variantMeta.push({
                    row: i + 2,
                    name: [nameColumn ? row[nameColumn.index] : null, item.label].filter((v) => !isEmpty(v)).join(' — ') || (item.variant_id ? '#' + item.variant_id : ''),
                    raw: row,
                })
            })
        }
    }

    return {
        products, variants, productMeta, variantMeta, productHeaders, variantHeaders,
        productSheetName, variantSheetName: variantSheetName || null,
    }
}

// Excel limit: a cell holds at most 32767 characters (xlsx throws otherwise).
// longer texts are split into continuation columns right after the main column and joined back on import.
export const EXCEL_CELL_LIMIT = 32767
const CHUNK_SIZE = 30000

// files exported by the previous version cut long texts with this marker; such cells are skipped on import
const TRUNCATED_MARK = '…[متن کامل در سایت است؛ این خانه هنگام ورود نادیده گرفته می‌شود]'

function isTruncatedCell(value) {
    return typeof value === 'string' && value.endsWith(TRUNCATED_MARK)
}

function splitText(text) {
    const pieces = []
    let start = 0
    while (start < text.length) {
        let end = Math.min(start + CHUNK_SIZE, text.length)
        // never cut a surrogate pair (emoji etc.)
        if (end < text.length && /[\uD800-\uDBFF]/.test(text[end - 1])) end--
        pieces.push(text.slice(start, end))
        start = end
    }
    return pieces
}

function exportCell(column, value) {
    if (column.type === 'status') return Number(value) === 1 ? 'منتشر' : 'پیش‌نویس'
    if (column.type === 'stock') return Number(value) === 1 ? 'موجود' : 'ناموجود'
    if (column.type === 'quantity') return value === null || value === undefined || value === '' ? 'نامحدود' : Number(value)
    if (value !== null && typeof value === 'object') return Array.isArray(value) ? value.join(' | ') : JSON.stringify(value)
    if (column.type === 'money' || column.type === 'int' || column.type === 'number') {
        if (value === null || value === undefined || value === '') return null
        const number = Number(value)
        return Number.isFinite(number) ? number : value
    }
    return value === null || value === undefined ? '' : value
}

function buildSheet(XLSX, columns, rows) {
    const values = rows.map((row) => columns.map((column) => exportCell(column, row[column.key])))
    // number of cells each column needs (continuation columns only when a value is too long)
    const partsPerColumn = columns.map((column, c) => values.reduce((max, row) => {
        const value = row[c]
        return typeof value === 'string' && value.length > EXCEL_CELL_LIMIT ? Math.max(max, splitText(value).length) : max
    }, 1))
    const layout = []
    columns.forEach((column, c) => {
        for (let part = 1; part <= partsPerColumn[c]; part++) layout.push({column, c, part})
    })
    const data = [layout.map((item) => (item.part === 1 ? item.column.title : continuationTitle(item.column.title, item.part)))]
    values.forEach((row) => {
        const split = row.map((value, c) => (partsPerColumn[c] > 1 && typeof value === 'string' && value.length > EXCEL_CELL_LIMIT ? splitText(value) : [value]))
        data.push(layout.map((item) => {
            const piece = split[item.c][item.part - 1]
            return piece === undefined ? (item.part === 1 ? split[item.c][0] : '') : piece
        }))
    })
    const sheet = XLSX.utils.aoa_to_sheet(data)
    sheet['!cols'] = layout.map((item) => ({wch: item.column.width || 12}))
    sheet['!autofilter'] = {ref: XLSX.utils.encode_range({s: {r: 0, c: 0}, e: {r: Math.max(rows.length, 1), c: layout.length - 1}})}
    // thousands separator for money columns
    layout.forEach(({column, part}, c) => {
        if (column.type !== 'money' || part !== 1) return
        for (let r = 1; r <= rows.length; r++) {
            const cell = sheet[XLSX.utils.encode_cell({r, c})]
            if (cell && cell.t === 'n') cell.z = '#,##0'
        }
    })
    return sheet
}

/** build the RTL workbook with both sheets */
export function variantInfo(product) {
    const variation = Number(product.variation)
    if (variation === 1) return 'متغیر'
    if (variation === 2) return 'رزرو'
    return 'ساده'
}

export function buildProductWorkbook(XLSX, products, variants) {
    products = (products || []).map((product) => Object.assign({}, product, {variant_info: variantInfo(product)}))
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, buildSheet(XLSX, PRODUCT_COLUMNS, products || []), PRODUCT_SHEET)
    XLSX.utils.book_append_sheet(workbook, buildSheet(XLSX, VARIANT_COLUMNS, variants || []), VARIANT_SHEET)
    workbook.Workbook = workbook.Workbook || {}
    workbook.Workbook.Views = [{RTL: true}]
    return workbook
}

export const ERROR_COLUMN = 'خطا'

function rtlWorkbook(XLSX) {
    const workbook = XLSX.utils.book_new()
    workbook.Workbook = {Views: [{RTL: true}]}
    return workbook
}

function failedSheet(XLSX, headers, columns, failures) {
    const cleanHeaders = headers.map((h) => (h == null ? '' : h))
    // drop an old "خطا" column so the file can be fixed and re-imported again and again
    const errorIndex = cleanHeaders.findIndex((h) => headerKey(h) === headerKey(ERROR_COLUMN))
    const keep = cleanHeaders.map((_, i) => i).filter((i) => i !== errorIndex)
    const data = [keep.map((i) => cleanHeaders[i]).concat(ERROR_COLUMN)]
    failures.forEach((failure) => {
        const raw = failure.raw || []
        data.push(keep.map((i) => (raw[i] === undefined ? null : raw[i])).concat(failure.reason))
    })
    const sheet = XLSX.utils.aoa_to_sheet(data)
    const widths = {}
    columns.forEach((column) => { widths[headerKey(column.title)] = column.width })
    sheet['!cols'] = keep.map((i) => ({wch: widths[headerKey(cleanHeaders[i])] || 14})).concat({wch: 60})
    return sheet
}

/** workbook with only the failed rows (same headers as the uploaded sheets) + a "خطا" column; importable as-is */
export function buildFailedRowsWorkbook(XLSX, parsed, failures) {
    const workbook = rtlWorkbook(XLSX)
    const products = failures.filter((f) => f.sheet === 'product')
    const variants = failures.filter((f) => f.sheet === 'variant')
    const productHeaders = parsed.productHeaders && parsed.productHeaders.length ? parsed.productHeaders : PRODUCT_COLUMNS.map((c) => c.title)
    // the products sheet is always present (header only when no product failed) so the variants sheet is found by name
    XLSX.utils.book_append_sheet(workbook, failedSheet(XLSX, productHeaders, PRODUCT_COLUMNS, products), PRODUCT_SHEET)
    if (variants.length) {
        const variantHeaders = parsed.variantHeaders && parsed.variantHeaders.length ? parsed.variantHeaders : VARIANT_COLUMNS.map((c) => c.title)
        XLSX.utils.book_append_sheet(workbook, failedSheet(XLSX, variantHeaders, VARIANT_COLUMNS, variants), VARIANT_SHEET)
    }
    return workbook
}

/* ------------------------------------------------------------------ sample file */

export const GUIDE_SHEET = 'راهنما'

const SAMPLE_PRODUCTS = [
    // id, name, product type (reference only), language, brand, categories, price, offer, quantity, status, abstract, description, thumbnail, gallery, length, width, height, weight
    [null, 'نمونه - تی‌شرت آزمایشی آبی (محصول جدید)', 'ساده', 'فارسی', 'برند نمونه', 'پوشاک نمونه > تی‌شرت نمونه | پیشنهاد ویژه نمونه', '۱٬۲۵۰٬۰۰۰', '۱۵۰٬۰۰۰', 'نامحدود', 'منتشر',
        'شناسه خالی = ساخت محصول جدید', 'متن توضیحات آزمایشی', 'https://example.com/sample/tshirt-blue.jpg', 'https://example.com/sample/tshirt-blue-2.jpg | https://example.com/sample/tshirt-blue-3.jpg', 30, 25, 3, 250],
    [999001, 'نمونه - ماگ آزمایشی (ویرایش با شناسه)', 'ساده', 'فارسی', 'برند نمونه', 'لوازم آشپزخانه نمونه', 480000, 0, 12, 'پیش‌نویس',
        'شناسه پر = ویرایش همان محصول', '', '', '', '۱۲', '۹٫۵', '۱۰', '۳۵۰'],
    [null, 'نمونه - دفترچه آزمایشی (موجودی خالی)', 'ساده', 'فارسی', '', 'لوازم تحریر نمونه > دفتر نمونه', '95,000', '', '', 'منتشر',
        'موجودی خالی = نامحدود', '', '', '', null, null, null, null],
    [999002, 'نمونه - تست تنوع آزمایشی (طرح و رنگ)', 'متغیر', 'فارسی', 'برند نمونه', 'پوشاک نمونه', 0, 0, 0, 'منتشر',
        'محصول متغیر؛ تنوع‌ها در برگه «تنوع‌ها»', '', '', '', null, null, null, null],
    [null, 'نمونه - هودی آزمایشی (محصول جدید با تنوع)', 'متغیر', 'فارسی', 'برند نمونه', 'پوشاک نمونه > هودی نمونه', 0, 0, '', 'پیش‌نویس',
        'محصول جدید؛ تنوع‌هایش در برگه «تنوع‌ها» با «نام محصول» وصل شده‌اند', '', '', '', null, null, null, null],
]

const SAMPLE_VARIANTS = [
    // product_id, product_name, variant_id, label, price, offer, quantity, stock_status
    [999002, 'نمونه - تست تنوع آزمایشی (طرح و رنگ)', 999101, '', '۸۹۰٬۰۰۰', 0, 5, 'موجود'],
    [999002, 'نمونه - تست تنوع آزمایشی (طرح و رنگ)', null, 'طرح: طرح 1، رنگ: آبی', 900000, '50,000', 'نامحدود', 'موجود'],
    [999002, 'نمونه - تست تنوع آزمایشی (طرح و رنگ)', null, 'طرح: طرح 2، رنگ: قرمز', 950000, 0, '۳', ''],
    [999003, 'نمونه - کفش آزمایشی (تک‌ویژگی)', null, 'سایز: 42', 1500000, 0, 0, 'ناموجود'],
    [null, 'نمونه - هودی آزمایشی (محصول جدید با تنوع)', null, 'طرح: طرح 1، رنگ: آبی', '۱٬۸۰۰٬۰۰۰', 0, 10, ''],
    [null, 'نمونه - هودی آزمایشی (محصول جدید با تنوع)', null, 'طرح: طرح 1، رنگ: قرمز', '1,850,000', '100,000', 'نامحدود', ''],
]

const GUIDE_ROWS = [
    ['بخش', 'ستون / مورد', 'توضیح و مقادیر مجاز'],
    ['کلی', 'ساختار فایل', 'برگه «محصولات» برای مشخصات محصول و برگه «تنوع‌ها» برای قیمت و موجودی تنوع‌هاست. این برگه (راهنما) هنگام ورود نادیده گرفته می‌شود. ترتیب ستون‌ها مهم نیست ولی نام سرستون‌ها را تغییر ندهید. فایل خروجی اکسل محصولات دقیقاً همین قالب را دارد.'],
    ['کلی', 'اعداد', 'ارقام فارسی (۱۲۳) و انگلیسی (123)، جداکننده هزارگان (1,250,000 یا ۱٬۲۵۰٬۰۰۰) و کلمه «تومان» پذیرفته می‌شود. اعشار با «.» یا «٫».'],
    ['کلی', 'داده‌های نمونه', 'همه ردیف‌های این فایل ساختگی‌اند (نام‌ها با «نمونه» شروع می‌شوند و شناسه‌های 999xxx واقعی نیستند). پیش از ورود، آن‌ها را پاک کنید یا با داده واقعی جایگزین کنید.'],
    ['کلی', 'متن‌های خیلی طولانی', 'هر خانه اکسل حداکثر 32767 نویسه جا دارد. متن‌های طولانی‌تر (مثلاً توضیحات) در خروجی در ستون‌های پشت‌سرهم «توضیحات (ادامه ۲)»، «توضیحات (ادامه ۳)» و ... بلافاصله بعد از ستون اصلی ادامه پیدا می‌کنند و هنگام ورود به ترتیب به هم چسبانده می‌شوند. این ستون‌ها را حذف یا جابه‌جا نکنید؛ برای ویرایش، متن را در همان خانه‌ها ادامه دهید.'],
    ['کلی', 'ستون «خطا»', 'در فایل ردیف‌های ناموفق، ستون «خطا» علت را نشان می‌دهد. ردیف‌ها را اصلاح و همان فایل را دوباره وارد کنید؛ ستون «خطا» خودکار نادیده گرفته می‌شود.'],
    ['محصولات', 'شناسه', 'عدد = ویرایش محصول با همین شناسه. خالی = جستجو با «نام» (فاصله‌های اضافه، ی/ک عربی و ارقام فارسی یکسان در نظر گرفته می‌شوند): اگر یک محصول با همین نام باشد ویرایش می‌شود، اگر نباشد محصول جدید ساخته می‌شود و اگر چند محصول هم‌نام باشد ردیف خطا می‌گیرد (شناسه را وارد کنید).'],
    ['محصولات', 'نام', 'الزامی.'],
    ['محصولات', 'نوع محصول', 'فقط برای اطلاع (در خروجی پر می‌شود) و هنگام ورود نادیده گرفته می‌شود: «ساده»، «متغیر» (قیمت و موجودی تنوع‌ها در برگه «تنوع‌ها») یا «رزرو». برای تغییر نوع محصول از صفحه ویرایش محصول استفاده کنید.'],
    ['محصولات', 'زبان', 'الزامی؛ نام زبان مثل «فارسی». اگر وجود نداشته باشد ساخته می‌شود.'],
    ['محصولات', 'برند', 'اختیاری؛ نام برند. اگر وجود نداشته باشد ساخته می‌شود.'],
    ['محصولات', 'دسته‌بندی', 'مسیر دسته با «>» از والد به فرزند و چند دسته با «|». مثال: «پوشاک > تی‌شرت | پیشنهاد ویژه». دسته‌های ناموجود ساخته می‌شوند و محصول فقط به آخرین دسته هر مسیر وصل می‌شود.'],
    ['محصولات', 'قیمت / تخفیف', 'عدد صحیح به تومان. تخفیف مبلغی است که از قیمت کم می‌شود (نه درصد). خالی = بدون مقدار.'],
    ['محصولات', 'موجودی', 'عدد = تعداد موجودی؛ «نامحدود» یا خالی = نامحدود؛ 0 = ناموجود.'],
    ['محصولات', 'وضعیت انتشار', 'الزامی: «منتشر» یا «پیش‌نویس» (1/0 و فعال/غیرفعال هم پذیرفته می‌شود).'],
    ['محصولات', 'توضیح کوتاه / توضیحات', 'متن آزاد و اختیاری.'],
    ['محصولات', 'تصویر شاخص', 'یک آدرس تصویر (URL). خالی = تصویر فعلی تغییر نمی‌کند.'],
    ['محصولات', 'گالری', 'چند آدرس تصویر که با «|» جدا شده‌اند. فقط تصاویری اضافه می‌شوند که هنوز به محصول وصل نیستند.'],
    ['محصولات', 'طول / عرض / ارتفاع / وزن', 'اعداد اختیاری؛ ابعاد می‌تواند اعشاری باشد، وزن عدد صحیح است.'],
    ['تنوع‌ها', 'شناسه محصول', 'شناسه محصول متغیر. اگر خالی باشد، محصول با «نام محصول» پیدا می‌شود.'],
    ['تنوع‌ها', 'نام محصول', 'وقتی «شناسه محصول» خالی است، محصول با همین نام (مثل برگه محصولات؛ شامل محصولاتی که در همین فایل ساخته می‌شوند) پیدا می‌شود. اگر محصولی پیدا نشود یا چند محصول هم‌نام باشد، ردیف خطا می‌گیرد. وقتی شناسه پر است فقط برای نمایش است.'],
    ['تنوع‌ها', 'وضعیت موجودی', 'فقط برای نمایش؛ هنگام ورود نادیده گرفته می‌شود.'],
    ['تنوع‌ها', 'شناسه تنوع', 'شناسه تنوع موجود (از فایل خروجی). اگر ستون «تنوع» خالی باشد، تنوع با همین شناسه ویرایش می‌شود.'],
    ['تنوع‌ها', 'تنوع', 'به شکل «ویژگی: مقدار»؛ چند ویژگی با «،» یا «,» و به ترتیب ویژگی‌های محصول. مثال: «طرح: طرح 1، رنگ: آبی». اگر ویژگی یا مقداری وجود نداشته باشد، خودکار ساخته می‌شود (ویژگی از نوع لیست، با زبان محصول)؛ تطبیق نام‌ها با یکسان‌سازی فاصله، ی/ک و ارقام انجام می‌شود تا تکراری ساخته نشود و تعداد ویژگی/مقدارهای ساخته‌شده در گزارش پایانی نمایش داده می‌شود. ویژگی متنی قابل استفاده نیست. برای محصولی که تنوع دارد، ترتیب و تعداد ویژگی‌ها باید با تنوع‌های فعلی یکی باشد؛ برای محصول بدون تنوع، اولین ردیف ساختار را تعیین می‌کند.'],
    ['تنوع‌ها', 'قیمت / تخفیف / موجودی', 'مثل برگه محصولات. موجودی «نامحدود» یا خالی = نامحدود؛ تخفیف خالی = 0.'],
    ['حالت‌ها', 'ویرایش با شناسه تنوع', 'شناسه تنوع پر و «تنوع» خالی ← قیمت، تخفیف و موجودی همان تنوع به‌روز می‌شود (ردیف 2 برگه تنوع‌ها).'],
    ['حالت‌ها', 'ویرایش با عنوان', 'شناسه تنوع خالی و «تنوع» برابر یک تنوع موجود ← همان تنوع به‌روز می‌شود و تنوع تکراری ساخته نمی‌شود.'],
    ['حالت‌ها', 'ساخت تنوع جدید', 'شناسه تنوع خالی و «تنوع» جدید ← تنوع جدید با همان ساختار صفحه ویرایش محصول ساخته می‌شود (ردیف 3 و 4). اگر شناسه تنوع پر باشد ولی «تنوع» با آن فرق کند، «تنوع» ملاک است.'],
    ['حالت‌ها', 'چند ویژگی / تک‌ویژگی', '«طرح: X، رنگ: Y» ← زنجیره طرح ← رنگ ساخته می‌شود. تک‌ویژگی مثل «سایز: 42» هم مجاز است (ردیف 5).'],
    ['حالت‌ها', 'محصول جدید همراه با تنوع', 'محصول را در برگه محصولات بدون شناسه بنویسید و در برگه تنوع‌ها «شناسه محصول» را خالی بگذارید و «نام محصول» را دقیقاً همان نام بنویسید (ردیف 6 برگه محصولات و ردیف‌های 6 و 7 برگه تنوع‌ها). ابتدا محصولات ثبت می‌شوند، سپس تنوع‌ها ساخته شده و محصول متغیر می‌شود.'],
    ['حالت‌ها', 'خطاها', 'ردیف ناموفق ثبت نمی‌شود و بقیه ردیف‌ها ادامه پیدا می‌کنند. در پایان، گزارش کامل در همان پنجره نمایش داده می‌شود و می‌توانید فایل ردیف‌های ناموفق را دانلود کنید.'],
]

/** sample workbook: both data sheets with every supported case + a Persian guide sheet */
export function buildSampleWorkbook(XLSX) {
    const workbook = rtlWorkbook(XLSX)
    const productSheet = XLSX.utils.aoa_to_sheet([PRODUCT_COLUMNS.map((c) => c.title)].concat(SAMPLE_PRODUCTS))
    productSheet['!cols'] = PRODUCT_COLUMNS.map((c) => ({wch: c.width || 12}))
    const variantSheet = XLSX.utils.aoa_to_sheet([VARIANT_COLUMNS.map((c) => c.title)].concat(SAMPLE_VARIANTS))
    variantSheet['!cols'] = VARIANT_COLUMNS.map((c) => ({wch: c.width || 12}))
    const guideSheet = XLSX.utils.aoa_to_sheet(GUIDE_ROWS)
    guideSheet['!cols'] = [{wch: 12}, {wch: 26}, {wch: 120}]
    XLSX.utils.book_append_sheet(workbook, productSheet, PRODUCT_SHEET)
    XLSX.utils.book_append_sheet(workbook, variantSheet, VARIANT_SHEET)
    XLSX.utils.book_append_sheet(workbook, guideSheet, GUIDE_SHEET)
    return workbook
}
